import React from 'react';
import { TestId } from '@components/test-ids';
import Grid from '@mui/material/Grid';
import { Place } from '@model/iceberg';
import { HotelTile } from '@model/iceberg/hotel-content/hotel-content';
import { DestinationLinkBuilder, LinkItem } from '@model/iceberg/geography';
import { DealFinderTile } from '@components/common/deal-finder-tile/DealFinderTile';
import { useI18NextContext } from '@components/hooks';
import { Result } from '@model/iceberg/deal-finder/deal-finder';
import { useRouter } from 'next/router';
import { GEO_LEVEL_ONE_COUNTRIES } from '@model/iceberg/geography/destination';
import { useDispatch } from 'react-redux';
import { resetDealFinder } from '@state/deal-finder/dealFinderOperations';
import { Carousel } from '@components/common';
import { checkInDateToQuery } from '@util/deal-finder';

type ResultPlace = Pick<Place, 'region' | 'resort'>;

export interface ProductPackageGroupProps {
  destination: string;
  path: string;
  packages: Array<HotelTile>;
  className?: string;
  place?: ResultPlace;
}

const getMediumTileSizeConfig = (size: number = 0) => {
  switch (size) {
    case 1:
      return 12;
    case 2:
      return 6;
    case 3:
      return 4;
    case 4:
    case 5:
      return 3;
    default:
      return 12;
  }
};

export function getResultFromHotelTile(
  {
    price,
    boardBasis: mealPlan,
    hotelRating,
    place: hotelPlace,
    connectId,
    checkInDate: startDate,
    featuredImage,
    gallery,
    nights,
    tripAdvisorRating
  }: HotelTile,
  place?: ResultPlace
): Result {
  return {
    leadInPrice: {
      perPerson: price,
      packageReferences: [],
      mealPlan,
      total: price
    },
    summary: {
      place: {
        ...place,
        ...hotelPlace
      },
      hotelRating: parseInt(hotelRating),
      tripAdvisorRating,
      nights,
      gallery,
      featuredImage,
      startDate,
      connectId
    },
    otherPrices: []
  };
}

export const ProductPackageGroup = ({ packages, destination, path, className, place }: ProductPackageGroupProps) => {
  if (!packages || packages.length === 0) return null;
  const t: any = useI18NextContext();
  const router = useRouter();
  const dispatch = useDispatch();
  const showCta: boolean = packages.length > 4 && !GEO_LEVEL_ONE_COUNTRIES.includes(destination);
  const filteredTiles: Array<HotelTile> = packages.slice(0, 4);
  const handleOnClick = (path?: string, date?: string) => {
    const search = checkInDateToQuery(date);
    const url = new DestinationLinkBuilder().getDestinationLink(path);
    router.push(`${url.as}${search ? `?${search}` : ''}`);
  };
  const onViewAllClick = () => {
    dispatch(resetDealFinder());
  };

  const linkBuilder: DestinationLinkBuilder = new DestinationLinkBuilder();
  const productLink: LinkItem = linkBuilder.getDestinationHotelsLink(path);

  const link = showCta
    ? { label: t('popular-hotels__cta--label', { destination }), linkItem: productLink, onClick: onViewAllClick }
    : undefined;

  return (
    <Carousel
      className={className}
      testId={TestId.productPackageGroup.main}
      title={t('popular-hotels__title--label', { destination })}
      link={link}
    >
      {filteredTiles.map((tile: HotelTile) => (
        <Grid key={tile.place.hotel?.path} item xs={12} sm={6} md={getMediumTileSizeConfig(filteredTiles.length)}>
          <DealFinderTile
            result={getResultFromHotelTile(tile, place)}
            onClick={() => handleOnClick(tile.place.hotel?.path, tile.checkInDate)}
          />
        </Grid>
      ))}
    </Carousel>
  );
};
